import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, CheckCircle, CircleSlash, Crown, Clock, Calendar, X, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

interface CompanyFiltersProps {
  onSearchChange: (value: string) => void;
  onFilterChange: (value: 'all' | 'active' | 'inactive') => void;
  onLicenseTypeChange: (value: 'all' | 'premium' | 'trial') => void;
  onExpiryStatusChange: (value: 'all' | 'expired' | 'valid') => void;
  onExpiringSoonChange: (value: boolean) => void;
  filterValue: 'all' | 'active' | 'inactive';
  licenseTypeValue: 'all' | 'premium' | 'trial';
  expiryStatusValue: 'all' | 'expired' | 'valid';
  expiringSoonValue: boolean;
  onAddCompany?: () => void;
}

export function CompanyFilters({
  onSearchChange,
  onFilterChange,
  onLicenseTypeChange,
  onExpiryStatusChange,
  onExpiringSoonChange,
  filterValue,
  licenseTypeValue,
  expiryStatusValue,
  expiringSoonValue,
  onAddCompany,
}: CompanyFiltersProps) {
  // Dropdown açık/kapalı durumu
  const [open, setOpen] = useState(false);
  
  // Filtreleri sıfırla ve menüyü kapat
  const resetFilters = () => {
    onFilterChange('all');
    onLicenseTypeChange('all');
    onExpiryStatusChange('all');
    onExpiringSoonChange(false);
    setOpen(false);
  };

  // Herhangi bir filtre aktif mi?
  const isAnyFilterActive = 
    filterValue !== 'all' || 
    licenseTypeValue !== 'all' || 
    expiryStatusValue !== 'all' || 
    expiringSoonValue;
    
  // Tıklama işleyicileri - değerleri değiştir ve menüyü kapat
  const handleFilterChange = (value: 'all' | 'active' | 'inactive') => {
    // Diğer filtreleri temizle, sadece seçilen filtre tipi aktif olsun
    onFilterChange(value);
    onLicenseTypeChange('all');
    onExpiryStatusChange('all');
    onExpiringSoonChange(false);
    setOpen(false);
  };
  
  const handleLicenseTypeChange = (value: 'all' | 'premium' | 'trial') => {
    // Diğer filtreleri temizle, sadece seçilen lisans tipi aktif olsun
    onFilterChange('all');
    onLicenseTypeChange(value);
    onExpiryStatusChange('all');
    onExpiringSoonChange(false);
    setOpen(false);
  };
  
  const handleExpiryStatusChange = (value: 'all' | 'expired' | 'valid') => {
    // Diğer filtreleri temizle, sadece seçilen süre durumu aktif olsun
    onFilterChange('all');
    onLicenseTypeChange('all');
    onExpiryStatusChange(value);
    onExpiringSoonChange(false);
    setOpen(false);
  };
  
  const handleExpiringSoonChange = (value: boolean) => {
    // Diğer filtreleri temizle, sadece yakında bitecek filtresi aktif olsun
    if (value) {
      onFilterChange('all');
      onLicenseTypeChange('all');
      onExpiryStatusChange('all');
    }
    onExpiringSoonChange(value);
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="space-y-4">
        {/* Arama ve Filtre */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Firma adı veya telefon ile arayın..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50 ${isAnyFilterActive ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100" : ""}`}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filtrele
                {isAnyFilterActive && (
                  <span className="ml-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {[filterValue !== 'all', licenseTypeValue !== 'all', expiryStatusValue !== 'all', expiringSoonValue].filter(Boolean).length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-2">
              {/* Lisans Durum Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Lisans Durumu
              </div>
              <DropdownMenuItem
                onClick={() => handleFilterChange(filterValue === 'active' ? 'all' : 'active')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${filterValue === 'active' ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <CheckCircle className={`mr-3 h-4 w-4 ${filterValue === 'active' ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className={filterValue === 'active' ? 'font-medium text-green-700' : 'text-slate-700'}>Aktif Lisans</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleFilterChange(filterValue === 'inactive' ? 'all' : 'inactive')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${filterValue === 'inactive' ? 'bg-red-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <CircleSlash className={`mr-3 h-4 w-4 ${filterValue === 'inactive' ? 'text-red-600' : 'text-slate-400'}`} />
                  <span className={filterValue === 'inactive' ? 'font-medium text-red-700' : 'text-slate-700'}>Pasif Lisans</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              {/* Lisans Tip Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Lisans Tipi
              </div>
              <DropdownMenuItem
                onClick={() => handleLicenseTypeChange(licenseTypeValue === 'premium' ? 'all' : 'premium')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${licenseTypeValue === 'premium' ? 'bg-amber-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Crown className={`mr-3 h-4 w-4 ${licenseTypeValue === 'premium' ? 'text-amber-600' : 'text-slate-400'}`} />
                  <span className={licenseTypeValue === 'premium' ? 'font-medium text-amber-700' : 'text-slate-700'}>Premium</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleLicenseTypeChange(licenseTypeValue === 'trial' ? 'all' : 'trial')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${licenseTypeValue === 'trial' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Clock className={`mr-3 h-4 w-4 ${licenseTypeValue === 'trial' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className={licenseTypeValue === 'trial' ? 'font-medium text-blue-700' : 'text-slate-700'}>Trial</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              {/* Süre Durum Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Lisans Süresi
              </div>
              <DropdownMenuItem
                onClick={() => handleExpiringSoonChange(!expiringSoonValue)}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${expiringSoonValue ? 'bg-orange-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Calendar className={`mr-3 h-4 w-4 ${expiringSoonValue ? 'text-orange-600' : 'text-slate-400'}`} />
                  <span className={expiringSoonValue ? 'font-medium text-orange-700' : 'text-slate-700'}>30 Gün İçinde Bitecek</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleExpiryStatusChange(expiryStatusValue === 'expired' ? 'all' : 'expired')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${expiryStatusValue === 'expired' ? 'bg-gray-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <X className={`mr-3 h-4 w-4 ${expiryStatusValue === 'expired' ? 'text-gray-600' : 'text-slate-400'}`} />
                  <span className={expiryStatusValue === 'expired' ? 'font-medium text-gray-700' : 'text-slate-700'}>Süresi Dolmuş</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleExpiryStatusChange(expiryStatusValue === 'valid' ? 'all' : 'valid')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${expiryStatusValue === 'valid' ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <CheckCircle className={`mr-3 h-4 w-4 ${expiryStatusValue === 'valid' ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className={expiryStatusValue === 'valid' ? 'font-medium text-green-700' : 'text-slate-700'}>Süresi Geçerli</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {onAddCompany && (
            <Button
              onClick={onAddCompany}
              className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Yeni Firma Ekle
            </Button>
          )}
        </div>

        {/* Aktif filtre belirteçleri */}
        {isAnyFilterActive && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <span className="text-xs font-medium text-slate-600 flex items-center">
              Aktif Filtreler:
            </span>
            {filterValue === 'active' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                <CheckCircle className="h-3 w-3" />
                Aktif Lisans
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                  onClick={() => onFilterChange('all')}
                />
              </Badge>
            )}

            {filterValue === 'inactive' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-800 border-0 hover:bg-red-200">
                <CircleSlash className="h-3 w-3" />
                Pasif Lisans
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-red-600"
                  onClick={() => onFilterChange('all')}
                />
              </Badge>
            )}

            {licenseTypeValue === 'premium' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 border-0 hover:bg-amber-200">
                <Crown className="h-3 w-3" />
                Premium
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-amber-600"
                  onClick={() => onLicenseTypeChange('all')}
                />
              </Badge>
            )}

            {licenseTypeValue === 'trial' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 border-0 hover:bg-blue-200">
                <Clock className="h-3 w-3" />
                Trial
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-blue-600"
                  onClick={() => onLicenseTypeChange('all')}
                />
              </Badge>
            )}

            {expiryStatusValue === 'expired' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-800 border-0 hover:bg-gray-200">
                <X className="h-3 w-3" />
                Süresi Dolmuş
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-gray-600"
                  onClick={() => onExpiryStatusChange('all')}
                />
              </Badge>
            )}

            {expiryStatusValue === 'valid' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                <CheckCircle className="h-3 w-3" />
                Süresi Geçerli
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                  onClick={() => onExpiryStatusChange('all')}
                />
              </Badge>
            )}

            {expiringSoonValue && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-800 border-0 hover:bg-orange-200">
                <Calendar className="h-3 w-3" />
                30 Gün İçinde Bitecek
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-orange-600"
                  onClick={() => onExpiringSoonChange(false)}
                />
              </Badge>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-7 px-3 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-200"
            >
              Tümünü Temizle
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
